import { ReactElement, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore, useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconOutput } from '@components/icons/output';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { Select } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import { AttributeType } from 'api/attributes/requests';
import { CompanyUserOptions } from 'api/authentication/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import {
  JourneyActionDetailed,
  JourneyField,
  JourneyType,
  TravellerJourneyActionDetailed,
} from 'api/types';
import { useUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';
import {
  ActionFooter,
  DescriptionFormField,
  SidebarContentLoadingSpinner,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import {
  AttributeFieldType,
  BaseContentProps,
  FieldMappedAttributeField,
  InfoBoxClosable,
  InfoBoxVariant,
  getFieldMappedAttributeFieldComparatorIds,
} from '../common';

export type Data = {
  description: string;
  fields: JourneyField[];
};

const Form = createForm<Data>();

type AvailableActionTypes = 'update_fields';

type UpdateFieldsFormContentProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  journeyId?: JourneyType['shortId'];
  filePath: string;
  onSubmit({ fields, description }: { fields: JourneyField[]; description: string }): Promise<any>;
  onCancel(): void;
  hideActionButtons?: boolean;
};

export const UpdateFieldsFormContent = <T extends AvailableActionTypes>({
  action,
  journeyId = '',
  filePath,
  onCancel,
  onSubmit,
  hideActionButtons,
}: UpdateFieldsFormContentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const attributeTemplatesQuery = useJourneyAttributes(journeyId);
  const attributes = attributeTemplatesQuery.data ?? [];
  const userQuery = useUsers('?ignore_invited=true');
  const usersList = userQuery.data;

  const initialFields = useRef(action.values?.fields ?? []);
  const [fields, setFields] = useState<JourneyField[]>(
    initialFields.current.map((field) => ({ ...field, id: uuid() }))
  );
  const isDirty = formStore.useIsDirty() || initialFields.current.length !== fields.length;

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['phone'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['date'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['select'],
  });
  const fileMultiVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file', 'file_multi'],
  });
  const numberVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number'],
  });
  const decimalVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number', 'decimal'],
  });
  const formValues = formStore.useFormValues();

  const allFields = fields.map(
    (field) => formValues[getComparatorIds(field.id).elementId] ?? ''
  ) as string[];

  const handleSubmit = () => {
    const formFields = fields.map((field) => {
      const { elementId, lastTitleId, lastTypeId, lastOptionsId } = getComparatorIds(field.id);
      const { valueId } = getFieldMappedAttributeFieldComparatorIds(field.id);
      return {
        id: field.id,
        field: formValues[elementId],
        value: formValues[valueId],
        lastSavedTitle: formValues[lastTitleId],
        lastSavedType: formValues[lastTypeId],
        lastSavedOptions: formValues[lastOptionsId],
      } as JourneyField;
    });
    return onSubmit({ fields: formFields, description: formValues['description'] as string });
  };

  if (attributeTemplatesQuery.isLoading || userQuery.isLoading) {
    return <SidebarContentLoadingSpinner />;
  }

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('fields')}>
          <Stack space={4}>
            <UpdateFieldsInfoBoxClosable infoType="selectField" />
            {fields.map((field, idx) => (
              <FieldComponent
                key={field.id}
                attributes={attributes}
                allFields={allFields}
                variableGroups={variableGroups}
                emailVariableGroups={emailVariableGroups}
                phoneVariableGroups={phoneVariableGroups}
                dateVariableGroups={dateVariableGroups}
                optionsVariableGroups={optionsVariableGroups}
                fileVariableGroups={fileVariableGroups}
                fileMultiVariableGroups={fileMultiVariableGroups}
                numberVariableGroups={numberVariableGroups}
                decimalVariableGroups={decimalVariableGroups}
                onRemove={() => setFields(fields.filter((c) => c.id !== field.id))}
                field={field}
                form={Form}
                usersList={usersList}
                filePath={filePath}
                journeyId={journeyId}
              />
            ))}
            <Stack orientation="horizontal">
              <Button
                onClick={() =>
                  setFields([
                    ...fields,
                    {
                      id: uuid(),
                      field: undefined,
                      value: undefined,
                    },
                  ])
                }
                text={t('addField')}
                iconLeft={<IconAddPlus />}
                variant="secondary"
              />
            </Stack>
            {fields.length > 0 && <UpdateFieldsInfoBox infoType="fieldsUpdated" />}
          </Stack>
        </HideContainer>
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty || fields.length === 0}
        hideActionButtons={hideActionButtons}
        onCancel={onCancel}
        form={Form}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const getComparatorIds = (id: string) => ({
  elementId: `${id}Element` as keyof Data,
  lastTitleId: `${id}Title` as keyof Data,
  lastTypeId: `${id}Type` as keyof Data,
  lastOptionsId: `${id}Options` as keyof Data,
});

type FieldComponentProps<T> = {
  attributes: AttributeFieldType[];
  allFields: string[];
  variableGroups: FieldMappingGroup[];
  emailVariableGroups: FieldMappingGroup[];
  phoneVariableGroups: FieldMappingGroup[];
  dateVariableGroups: FieldMappingGroup[];
  optionsVariableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  numberVariableGroups: FieldMappingGroup[];
  decimalVariableGroups: FieldMappingGroup[];
  field: JourneyField;
  onRemove: () => void;
  form: FormInstance<T>;
  usersList?: User[];
  filePath: string;
  journeyId: string;
};

const HiddenContent = styled.div<{ hidden: boolean }>((p) => ({
  visibility: p.hidden ? 'hidden' : 'visible',
}));

export const FieldComponent = ({
  attributes,
  allFields,
  variableGroups,
  emailVariableGroups,
  phoneVariableGroups,
  dateVariableGroups,
  optionsVariableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  numberVariableGroups,
  decimalVariableGroups,
  field,
  onRemove,
  form: Form,
  usersList,
  filePath,
  journeyId,
}: FieldComponentProps<Data>) => {
  const { t } = useScopedTranslate(locale);
  const [selectedField, setSelectedField] = useState(field.field);
  const [defaultValue, setDefaultValue] = useState(field.value);
  const { elementId, lastTitleId, lastTypeId, lastOptionsId } = getComparatorIds(field.id);
  const { store } = useFormContext();
  const element = store.useFieldValue(elementId) as string;
  const options = (attributes as AttributeType[])
    .filter(
      (attribute) =>
        !allFields.find((afield) => afield === attribute.shortId) ||
        attribute.shortId === selectedField
    )
    .filter((attribute) => attribute.isActive || attribute.shortId === selectedField)
    .map((option) => ({
      value: option.shortId,
      label: option.title,
    }));

  const selectedAttribute = (attributes as AttributeType[]).find(
    (attribute) => attribute.shortId === element
  );
  const isActive = selectedAttribute?.isActive ?? false;
  // Follow the live selection rather than the saved one, so clearing the select
  // returns the card to its empty state instead of the missing-field state.
  const hasSelection = !!element;
  const hideDropdown = hasSelection && (!selectedAttribute || !isActive);

  useEffect(() => {
    store.actions.setValue(lastTitleId, selectedAttribute?.title ?? '');
    store.actions.setValue(lastTypeId, selectedAttribute?.attributeType ?? '');
    store.actions.setValue(lastOptionsId, selectedAttribute?.options ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttribute]);

  const handleFieldChange = (newField: string) => {
    if (newField !== selectedField) {
      setSelectedField(newField);
      if (newField === field.field) {
        setDefaultValue(field.value);
      } else {
        setDefaultValue(undefined);
      }
    }
  };

  return (
    <Card space={2}>
      <Stack space={2}>
        {
          <Stack space={2} orientation="horizontal">
            <div style={{ flexGrow: 1 }}>
              <HiddenContent hidden={hideDropdown}>
                <Form.Select
                  // Force a rerender when an attribute is deleted that is being used
                  key={`${field.field}-${attributes.length}`}
                  id={elementId}
                  label={t('field')}
                  options={options}
                  defaultValue={field.field}
                  validators={['required']}
                  onChange={handleFieldChange}
                />
              </HiddenContent>
              {hideDropdown && (
                <Select
                  key={`not-found-${Boolean(selectedAttribute)}`}
                  label={t('field')}
                  defaultValue="0"
                  isDisabled
                  options={[
                    {
                      value: '0',
                      label: (
                        <Stack orientation="horizontal" space={1}>
                          <IconTextFormatLinkOff color="red100" />
                          <Text
                            color="red100"
                            text={
                              selectedAttribute?.title ??
                              field.last_saved_title ??
                              t('fieldNotFound')
                            }
                          />
                        </Stack>
                      ),
                    },
                  ]}
                />
              )}
            </div>
            <IconButton
              icon={<IconTrash />}
              variant="secondary-purple"
              onClick={onRemove}
              style={{ marginTop: pxToRem(28) }}
            />
          </Stack>
        }
        {(selectedAttribute || (hasSelection && field.last_saved_type)) && (
          <FieldMappedAttributeField
            key={`attribute-field-${Boolean(hideDropdown)}`}
            field={{ ...field, value: defaultValue }}
            attribute={selectedAttribute}
            usersList={usersList}
            form={Form}
            variableGroups={variableGroups}
            emailVariableGroups={emailVariableGroups}
            phoneVariableGroups={phoneVariableGroups}
            dateVariableGroups={dateVariableGroups}
            optionsVariableGroups={optionsVariableGroups}
            fileVariableGroups={fileVariableGroups}
            fileMultiVariableGroups={fileMultiVariableGroups}
            numberVariableGroups={numberVariableGroups}
            decimalVariableGroups={decimalVariableGroups}
            filePath={filePath}
            overwriteLabel={t('value')}
            overwriteValueRequired
            isDisabled={hideDropdown}
            savedAttributeType={field.last_saved_type}
            savedAttributeOptions={field.last_saved_options}
            journeyId={journeyId}
          />
        )}
      </Stack>
    </Card>
  );
};

type InfoType = 'selectField' | 'fieldsUpdated';

const TYPE_TO_VARIANT: Record<InfoType, InfoBoxVariant> = {
  selectField: 'blue',
  fieldsUpdated: 'indigo',
};

const TYPE_TO_TEXT: Record<InfoType, keyof typeof locale.en> = {
  selectField: 'infoSelectField',
  fieldsUpdated: 'infoFieldsUpdated',
};

type CloseableInfoType = 'selectField';

const TYPE_TO_OPTION_KEY: Record<CloseableInfoType, keyof CompanyUserOptions> = {
  selectField: 'hasHiddenUpdateFieldsSelectField',
};

const TYPE_TO_ICON: Record<InfoType, ReactElement<IconProps>> = {
  selectField: <IconAttributeJourney />,
  fieldsUpdated: <IconOutput />,
};

export const UpdateFieldsInfoBox = ({
  infoType,
  children,
}: {
  infoType: InfoType;
  children?: ReactElement;
}) => {
  const { t } = useScopedTranslate(locale);
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBox variant={variant} text={text} icon={icon} children={children} />;
};

export const UpdateFieldsInfoBoxClosable = ({ infoType }: { infoType: CloseableInfoType }) => {
  const { t } = useScopedTranslate(locale);
  const optionKey = TYPE_TO_OPTION_KEY[infoType];
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBoxClosable optionKey={optionKey} variant={variant} text={text} icon={icon} />;
};

const locale = {
  en: {
    actionDetails: 'Action details',
    fields: 'Fields',
    addField: 'Add field',
    field: 'Field',
    noDescription: 'No description',
    value: 'Value',
    infoSelectField: 'Select which journey field you want to update',
    infoFieldsUpdated:
      'When this action is run, the fields you selected will be updated with the new information you provided',
    fieldNotFound: 'Field not found',
  },
};
